import { Query, VirtualBrand } from '~/Api/Analytics/VirtualBrand';
import { VirtualBrandAnalytics } from '~/Api/Analytics/VirtualBrand/mapAnalyticVirtualBrand';
import Pagination from '~/Api/Pagination/Pagination';

import ActionTypes from './ActionTypes';

interface VirtualBrandsState {
	request: Promise<any>;
	isFetching: boolean;
	query: Query;
	items: VirtualBrand[] | null;
	pagination: Pagination | null;
}
interface AnalyticsVirtualBrandsState {
	fetchedBrands: Set<string>;
	items: Map<string, VirtualBrandAnalytics>;
}

export interface State {
	analyticsVirtualBrands: AnalyticsVirtualBrandsState | null;
	virtualBrands: VirtualBrandsState | null;
	groupAddRequest: Promise<any> | null;
}

export type VirtualBrandState = State;
export type RequestAnalyticsOfVirtualBrand = {
	type: ActionTypes.requestAnalyticsOfVirtualBrand;
	brandId: string;
};

type Action =
	| {
			type: ActionTypes.request;
			request: Promise<any>;
			query: Query;
	  }
	| {
			type: ActionTypes.receive;
			request: Promise<any>;
			query: Query;
			items: null;
			pagination: null;
	  }
	| {
			type: ActionTypes.add;
			item: VirtualBrand | null;
	  }
	| {
			type: ActionTypes.receive;
			request: Promise<any>;
			query: Query;
			items: VirtualBrand[];
			pagination: Pagination;
	  }
	| RequestAnalyticsOfVirtualBrand
	| {
			type: ActionTypes.addAnalyticsOfVirtualBrand;
			analyticVirtualBrand: VirtualBrandAnalytics;
	  }
	| {
			type: ActionTypes.requestAddGroupActualPacks;
			request: Promise<any>;
	  }
	| {
			type: ActionTypes.receiveAddGroupActualPacks;
			request: Promise<any>;
	  };

function createState(): State {
	return {
		analyticsVirtualBrands: null,
		virtualBrands: null,
		groupAddRequest: null,
	};
}

export default function reducer(state: State = createState(), action: Action): State {
	switch (action.type) {
		case ActionTypes.request:
			return {
				...state,
				virtualBrands: {
					...(selectVirtualBrands(state, action.query) || {
						items: null,
						pagination: null,
					}),
					request: action.request,
					isFetching: true,
					query: action.query,
				},
			};
		case ActionTypes.requestAnalyticsOfVirtualBrand:
			return {
				...state,
				analyticsVirtualBrands: {
					items: state.analyticsVirtualBrands?.items || new Map(),
					fetchedBrands: new Set(
						Array.from(state.analyticsVirtualBrands?.fetchedBrands || []).concat([action.brandId])
					),
				},
			};

		case ActionTypes.receive: {
			const virtualBrandsState = state.virtualBrands;
			if (!virtualBrandsState || virtualBrandsState?.request !== action.request) {
				return state;
			}
			if (!action.items) {
				return { ...state, virtualBrands: null };
			}

			const items = virtualBrandsState.items ? [...virtualBrandsState.items] : [];
			items.splice(action.pagination.getFirstIndex(), action.pagination.perPage, ...action.items);

			return {
				...state,
				virtualBrands: {
					...virtualBrandsState,
					isFetching: false,
					items,
					pagination: action.pagination,
				},
			};
		}
		case ActionTypes.add: {
			const virtualBrandsState = state.virtualBrands;

			if (!action.item) {
				return state;
			}

			const items = (virtualBrandsState?.items || []).concat([action.item]);

			return {
				...state,
				virtualBrands: {
					...virtualBrandsState,
					isFetching: false,
					request: Promise.resolve(),
					query: new Query([], null),
					items,
					pagination: virtualBrandsState?.pagination || new Pagination(1, 1, 1, 1),
				},
			};
		}

		case ActionTypes.addAnalyticsOfVirtualBrand: {
			const analyticsVirtualBrandsState = state.analyticsVirtualBrands;
			if (!analyticsVirtualBrandsState) {
				return state;
			}

			const nextMaps = analyticsVirtualBrandsState.items || new Map();

			nextMaps.set(action.analyticVirtualBrand.id, action.analyticVirtualBrand);

			return {
				...state,
				analyticsVirtualBrands: {
					...analyticsVirtualBrandsState,
					fetchedBrands: new Set(
						Array.from(state.analyticsVirtualBrands?.fetchedBrands || []).filter(
							(candidateBrandId) => candidateBrandId !== action.analyticVirtualBrand.id
						)
					),
					items: new Map(nextMaps),
				},
			};
		}

		case ActionTypes.requestAddGroupActualPacks:
			return { ...state, groupAddRequest: action.request };

		case ActionTypes.receiveAddGroupActualPacks:
			return {
				...state,
				groupAddRequest: action.request === state.groupAddRequest ? null : state.groupAddRequest,
			};

		default:
			return state;
	}
}

export function selectVirtualBrands(state: State, query: Query): VirtualBrandsState | null {
	return state.virtualBrands?.query.equals(query) ? state.virtualBrands : null;
}
